import { defineStore } from 'pinia'
import { getAllSpaces } from '@/api/spaces/space'
import { getSpaceContext, updateSpaceContext } from '@/api/v2/space_context'
import { useAccountStore } from '@/stores/account'
import { useAuthStore } from '@/stores/auth'
import { usePermissionStore } from '@/stores/permission'
import { getItem, removeItem, setItem } from '@/utils/storage'
import { filterStorageKey, STORAGE_TTL_SECONDS } from '@/components/ListFilter/useFilterUrl'
import {
  ALL_SPACES,
  canCreateIn,
  initialCurrent,
  pushRecent,
  readPersisted,
  serverContext,
  storageKey,
  toSpaces,
  SPACE_FILTER_PARAM,
  withContextSpace,
  type SpaceEntry,
} from './spaceContext'

/** The lists whose space filter follows the context. */
const CONTEXT_LISTS = ['tasks', 'protocols']

interface SpaceState {
  /** Employees only; contacts keep the plain menu and lists. */
  enabled: boolean
  ready: boolean
  spaces: SpaceEntry[]
  currentId: string
  recent: string[]
  isManager: boolean
  key: string | null
}

let readyPromise: Promise<void> | null = null
/** Bumped when a session ends; a space list still loading then belongs to the previous one. */
let epoch = 0

/**
 * The space the user is working in (#152): picked once in the top bar, it decides where "+ Neu"
 * creates and prefills the space filter of the task and protocol lists. Stored per user and account
 * in the backend (#238), so a logout or another device does not reset it; localStorage only keeps
 * a copy for when the backend cannot be reached.
 */
export const useSpaceStore = defineStore('space', {
  state: (): SpaceState => ({
    enabled: false,
    ready: false,
    spaces: [],
    currentId: ALL_SPACES,
    recent: [],
    isManager: false,
    key: null,
  }),

  getters: {
    current(state): SpaceEntry | null {
      return state.spaces.find((space) => space.id === state.currentId) ?? null
    },
    isAll(state): boolean {
      return state.currentId === ALL_SPACES
    },
    mySpaces(state): SpaceEntry[] {
      return state.spaces.filter((space) => space.isMember)
    },
    recentSpaces(state): SpaceEntry[] {
      return state.recent
        .map((id) => state.spaces.find((space) => space.id === id))
        .filter((space): space is SpaceEntry => !!space)
    },
    creatableSpaces(state): SpaceEntry[] {
      return state.spaces.filter((space) => canCreateIn(space, state.isManager))
    },
    /** The switcher only shows when there is something to switch between. */
    showSwitcher(state): boolean {
      return state.enabled && state.spaces.filter((space) => space.isMember).length > 1
    },
  },

  actions: {
    /** Loads once per session; later callers wait for the same promise. */
    ensureReady(): Promise<void> {
      if (!readyPromise) {
        const session = epoch
        readyPromise = this.load().catch(() => {
          // Without spaces the context is simply off; everything else keeps working.
          if (session === epoch) this.enabled = false
        }).finally(() => {
          if (session === epoch) this.ready = true
        })
      }
      return readyPromise
    },

    async load(): Promise<void> {
      const session = epoch
      const account = await useAccountStore().getCurrentAccount()
      if (session !== epoch) return
      const authStore = useAuthStore()
      const roles = usePermissionStore().roles || []
      if (!roles.includes('user') || !authStore.user_id || !account.data.account_id) {
        this.enabled = false
        return
      }
      this.isManager = roles.includes('manager')
      this.key = storageKey(account.data.account_id, authStore.user_id)

      const [response, stored] = await Promise.all([
        getAllSpaces({ listQuery: { limit: 1000 } }),
        getSpaceContext().then((r) => r.data.data).catch(() => null),
      ])
      if (session !== epoch) return
      this.spaces = toSpaces(response.data.data, authStore.user_id)
      const local = readPersisted(localStorage.getItem(this.key))
      const persisted = serverContext(stored) ?? local
      this.recent = persisted.recent.filter((id) => this.spaces.some((space) => space.id === id))
      this.currentId = initialCurrent(persisted, this.spaces)
      // First load after the backend learned it: hand over what this browser remembered.
      if (stored && !serverContext(stored) && local.currentId) this.persist()
      this.enabled = true
    },

    select(id: string): void {
      if (id !== ALL_SPACES && !this.spaces.some((space) => space.id === id)) return
      this.currentId = id
      this.recent = pushRecent(this.recent, id)
      this.persist()
      // The lists' stored filter follows, so coming back to a list shows the new space.
      CONTEXT_LISTS.forEach((resource) => {
        const key = filterStorageKey(resource)
        const next = withContextSpace(getItem(key) as Record<string, string> | null, id)
        if (next) setItem(key, next, STORAGE_TTL_SECONDS)
        else removeItem(key)
      })
    },

    persist(): void {
      if (!this.key) return
      try {
        localStorage.setItem(this.key, JSON.stringify({ currentId: this.currentId, recent: this.recent }))
      } catch {
        // A full or blocked storage only costs the local copy.
      }
      // Fire and forget: a failed save only means the next login starts from the previous one.
      updateSpaceContext({ current_id: this.currentId, recent: this.recent }).catch(() => {})
    },

    /** Prefill for a list without URL or stored filter: the current space, if one is chosen. */
    async listFallback(): Promise<Record<string, string> | null> {
      await this.ensureReady()
      if (!this.enabled || this.currentId === ALL_SPACES) return null
      return { [SPACE_FILTER_PARAM]: this.currentId }
    },
  },
})

/**
 * Ends the space session (logout, account switch): the next `ensureReady()` loads the spaces of
 * the new user/account instead of reusing the previous one's list and current space.
 */
export function resetSpaceSession(): void {
  epoch++
  readyPromise = null
  useSpaceStore().$reset()
}
